import { makeAutoObservable, type AnnotationsMap } from "mobx";

import type { RecordListItemDTO } from "@/application/records/dtos/RecordListItemDTO";
import type {
  RecordsStateChangeEvent,
  RecordsStateManagerConfig,
  RecordsStateSnapshot,
  RecordsStateSubscriber,
} from "./types/RecordsStateTypes";

const RECORD_FIELDS: Array<keyof RecordListItemDTO> = [
  "id",
  "name",
  "project",
  "type",
  "status",
  "participants",
  "participants_count",
  "creation_date",
  "modified_date",
  "content",
  "size_mb",
  "is_archived",
  "is_public",
  "file_extension",
  "file_name",
  "duration_sec",
];

export interface ProjectUpdate {
  recordIds: string[];
  projectName: string | null;
  projectId?: string;
  timestamp: number;
}

export class RecordsStateManager {
  private readonly records = new Map<string, RecordListItemDTO>();
  private readonly subscribers = new Set<RecordsStateSubscriber>();
  private readonly updateHistory: ProjectUpdate[] = [];
  private readonly maxHistorySize: number;
  private readonly enableLogging: boolean;

  private version = 0;
  private lastUpdated = 0;

  constructor(config?: RecordsStateManagerConfig) {
    this.maxHistorySize = config?.maxHistorySize ?? 1000;
    this.enableLogging = Boolean(config?.enableLogging);

    makeAutoObservable(
      this,
      {
        records: false,
        subscribers: false,
        updateHistory: false,
      } as AnnotationsMap<RecordsStateManager, never>,
      { autoBind: true }
    );
  }

  subscribe(callback: RecordsStateSubscriber): () => void {
    this.subscribers.add(callback);
    callback({
      version: this.version,
      timestamp: this.lastUpdated,
      updatedRecords: Array.from(this.records.values()).map((record) =>
        this.cloneRecord(record)
      ),
      removedRecordIds: [],
      records: this.records,
    });
    return () => {
      this.subscribers.delete(callback);
    };
  }

  initializeRecords(records: RecordListItemDTO[]): number {
    this.records.clear();
    const normalized = records
      .filter((record) => Boolean(record?.id))
      .map((record) => this.cloneRecord(record));

    for (const record of normalized) {
      this.records.set(record.id, record);
    }

    if (!normalized.length) {
      return 0;
    }

    return this.commitChange({
      updatedRecords: normalized,
      removedRecordIds: [],
    });
  }

  upsertRecords(records: RecordListItemDTO[]): number {
    if (!records.length) {
      return 0;
    }

    const updated: RecordListItemDTO[] = [];
    for (const record of records) {
      if (!record?.id) {
        continue;
      }
      const existing = this.records.get(record.id);
      if (!existing) {
        const clone = this.cloneRecord(record);
        this.records.set(clone.id, clone);
        updated.push(clone);
        continue;
      }

      const merged = this.mergeRecords(existing, record);
      if (this.hasRecordChanged(existing, merged)) {
        this.records.set(merged.id, merged);
        updated.push(merged);
      }
    }

    if (!updated.length) {
      return 0;
    }

    return this.commitChange({
      updatedRecords: updated,
      removedRecordIds: [],
    });
  }

  updateRecord(recordId: string, updates: Partial<RecordListItemDTO>): boolean {
    if (!recordId || !updates) {
      return false;
    }

    const existing = this.records.get(recordId);
    if (!existing) {
      return false;
    }

    const merged = this.mergeRecords(existing, updates);
    if (!this.hasRecordChanged(existing, merged)) {
      return false;
    }

    this.records.set(recordId, merged);
    this.commitChange({
      updatedRecords: [merged],
      removedRecordIds: [],
    });
    return true;
  }

  updateRecords(
    recordIds: string[],
    updates: Partial<RecordListItemDTO>
  ): number {
    if (!recordIds.length) {
      return 0;
    }

    const updated: RecordListItemDTO[] = [];
    for (const recordId of recordIds) {
      const existing = this.records.get(recordId);
      if (!existing) {
        continue;
      }
      const merged = this.mergeRecords(existing, updates);
      if (!this.hasRecordChanged(existing, merged)) {
        continue;
      }
      this.records.set(recordId, merged);
      updated.push(merged);
    }

    if (!updated.length) {
      return 0;
    }

    return this.commitChange({
      updatedRecords: updated,
      removedRecordIds: [],
    });
  }

  updateRecordsProject(
    recordIds: string[],
    projectName: string | null,
    projectId?: string
  ): number {
    if (!recordIds.length) {
      return 0;
    }

    // Если projectName null, но projectId передан, используем projectId
    // Для removeFromProject projectName = null, но projectId содержит ID проекта для удаления
    const finalProjectName = projectName || (projectId ? "" : "");
    const normalizedProject = this.normalizeProjectName(finalProjectName);

    const updated: RecordListItemDTO[] = [];
    for (const recordId of recordIds) {
      const existing = this.records.get(recordId);
      if (!existing) {
        continue;
      }

      if (existing.project === normalizedProject) {
        continue;
      }

      const merged = this.mergeRecords(existing, {
        project: normalizedProject,
      });
      this.records.set(recordId, merged);
      updated.push(merged);
    }

    if (!updated.length) {
      return 0;
    }

    this.recordProjectUpdate({
      recordIds,
      projectName: normalizedProject || null,
      projectId,
      timestamp: Date.now(),
    });

    return this.commitChange({
      updatedRecords: updated,
      removedRecordIds: [],
    });
  }

  removeRecords(recordIds: string[]): number {
    if (!recordIds.length) {
      return 0;
    }

    const removed: string[] = [];
    for (const recordId of recordIds) {
      if (this.records.delete(recordId)) {
        removed.push(recordId);
      }
    }

    if (!removed.length) {
      return 0;
    }

    return this.commitChange({
      updatedRecords: [],
      removedRecordIds: removed,
    });
  }

  getRecord(recordId: string): RecordListItemDTO | undefined {
    const record = this.records.get(recordId);
    return record ? this.cloneRecord(record) : undefined;
  }

  getAllRecords(): ReadonlyMap<string, RecordListItemDTO> {
    return this.records;
  }

  getUpdateHistory(): ProjectUpdate[] {
    return [...this.updateHistory];
  }

  getLatestProjectUpdateFor(recordId: string): ProjectUpdate | undefined {
    for (let index = this.updateHistory.length - 1; index >= 0; index--) {
      const update = this.updateHistory[index];
      if (update.recordIds.includes(recordId)) {
        return update;
      }
    }
    return undefined;
  }

  getStats() {
    return {
      totalRecords: this.records.size,
      version: this.version,
      lastUpdated: this.lastUpdated,
      updateHistorySize: this.updateHistory.length,
      subscribersCount: this.subscribers.size,
    };
  }

  createSnapshot(): RecordsStateSnapshot {
    const records: Record<string, RecordListItemDTO> = {};
    for (const [id, record] of this.records.entries()) {
      records[id] = this.cloneRecord(record);
    }
    return {
      records,
      version: this.version,
      lastUpdated: this.lastUpdated,
    };
  }

  restoreSnapshot(snapshot: RecordsStateSnapshot): void {
    const nextIds = new Set(Object.keys(snapshot.records));
    const removed: string[] = [];
    for (const currentId of this.records.keys()) {
      if (!nextIds.has(currentId)) {
        removed.push(currentId);
      }
    }

    this.records.clear();

    const updated: RecordListItemDTO[] = [];
    for (const [id, record] of Object.entries(snapshot.records)) {
      if (!id) {
        continue;
      }
      const clone = this.cloneRecord(record);
      this.records.set(id, clone);
      updated.push(clone);
    }

    this.commitChange({
      updatedRecords: updated,
      removedRecordIds: removed,
    });
  }

  reset(): void {
    if (!this.records.size && !this.updateHistory.length) {
      return;
    }

    const removedRecordIds = Array.from(this.records.keys());
    this.records.clear();
    this.updateHistory.length = 0;

    this.commitChange({
      updatedRecords: [],
      removedRecordIds,
    });
  }

  private commitChange(payload: {
    updatedRecords: RecordListItemDTO[];
    removedRecordIds: string[];
  }): number {
    const { updatedRecords, removedRecordIds } = payload;
    if (!updatedRecords.length && !removedRecordIds.length) {
      return 0;
    }

    this.version += 1;
    this.lastUpdated = Date.now();

    const change: RecordsStateChangeEvent = {
      version: this.version,
      timestamp: this.lastUpdated,
      updatedRecords: updatedRecords.map((record) => this.cloneRecord(record)),
      removedRecordIds: [...removedRecordIds],
      records: this.records,
    };

    for (const subscriber of this.subscribers) {
      try {
        subscriber(change);
      } catch (error) {
        console.error(
          "[RecordsStateManager] Error in subscriber:",
          error instanceof Error ? error.message : error
        );
      }
    }

    if (this.enableLogging) {
      this.logChange(change);
    }

    return updatedRecords.length + removedRecordIds.length;
  }

  private mergeRecords(
    current: RecordListItemDTO,
    updates: Partial<RecordListItemDTO>
  ): RecordListItemDTO {
    return {
      ...current,
      ...updates,
    };
  }

  private hasRecordChanged(
    existing: RecordListItemDTO,
    next: RecordListItemDTO
  ): boolean {
    for (const field of RECORD_FIELDS) {
      if (!Object.is(existing[field], next[field])) {
        return true;
      }
    }
    return false;
  }

  private cloneRecord(record: RecordListItemDTO): RecordListItemDTO {
    return { ...record };
  }

  private normalizeProjectName(projectName: string | null): string {
    if (!projectName) {
      return "";
    }
    return projectName.trim();
  }

  private recordProjectUpdate(update: ProjectUpdate): void {
    this.updateHistory.push(update);
    if (this.updateHistory.length > this.maxHistorySize) {
      this.updateHistory.splice(
        0,
        this.updateHistory.length - this.maxHistorySize
      );
    }
  }

  private logChange(change: RecordsStateChangeEvent): void {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[RecordsStateManager] Change:", {
        version: change.version,
        timestamp: change.timestamp,
        updated: change.updatedRecords.length,
        removed: change.removedRecordIds.length,
      });
    }
  }
}
